import React, { useCallback, useMemo, useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Combobox,
  Group,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
  useCombobox,
} from "@mantine/core";
import { IconPlus, IconTag, IconX } from "@tabler/icons-react";
import usePermissions from "../../../../hooks/usePermissions";
import {
  isDuplicateTagNameError,
  useAppointmentTags,
} from "../../../../hooks/useAppointmentTags";
import {
  AppointmentTag,
  MAX_APPOINTMENT_TAG_NAME_LENGTH,
} from "../../../../types/appointmentTags";
import TagEmojiPicker from "../../../appointmentTags/TagEmojiPicker";
import styles from "./AppointmentTagsRow.module.css";

const sameName = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

export interface AppointmentTagsRowProps {
  /** Transaction uuid. */
  transactionId: string;
  /** The appointment's current tags (`transaction.tags`). */
  tags: AppointmentTag[];
  /** The write landed: the tags after it (sync the calendar store). */
  onChange: (tags: AppointmentTag[]) => void;
}

/**
 * The appointment Tags row (design handoff §4, item 4): removable chips (emoji
 * or tag icon), then "+ Add tag" → an inline name input with the emoji picker
 * inside on the left and a dropdown of the location's unapplied tags (open
 * right away, filtered as you type, arrow keys + Enter pick), Add and cancel.
 * A typed name that matches a catalog tag applies it; a new name
 * creates the tag first, which needs `calendar.canManageAppointmentTags`
 * (applying / removing is ungated). Shared by the drawer and the mobile
 * detail sheet.
 */
export default function AppointmentTagsRow({
  transactionId,
  tags,
  onChange,
}: AppointmentTagsRowProps) {
  const { listTags, createTag, setTransactionTags } = useAppointmentTags();
  const canCreate =
    usePermissions()?.calendar?.canManageAppointmentTags === true;

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [emoji, setEmoji] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<AppointmentTag[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const reset = () => {
    combobox.closeDropdown();
    setAdding(false);
    setDraft("");
    setEmoji(null);
  };

  const loadCatalog = useCallback(async () => {
    try {
      const list = await listTags();
      setCatalog(list);
      return list;
    } catch (e) {
      console.error("Failed to load appointment tags:", e);
      setCatalog([]);
      return [];
    }
  }, [listTags]);

  const start = () => {
    setError(null);
    setAdding(true);
    combobox.openDropdown();
    if (catalog === null) void loadCatalog();
  };

  const write = async (next: AppointmentTag[]) => {
    setSaving(true);
    setError(null);
    try {
      onChange(
        await setTransactionTags(
          transactionId,
          next.map((t) => t.id),
        ),
      );
      return true;
    } catch (e) {
      console.error("Failed to save appointment tags:", e);
      setError("Couldn't save tags. Try again.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const remove = (tag: AppointmentTag) =>
    void write(tags.filter((t) => t.id !== tag.id));

  const apply = async (tag: AppointmentTag) => {
    if (tags.some((t) => t.id === tag.id)) {
      reset();
      return;
    }
    if (await write([...tags, tag])) reset();
  };

  const commit = async (raw: string = draft) => {
    const name = raw.trim();
    if (!name) {
      reset();
      return;
    }
    if (tags.some((t) => sameName(t.name, name))) {
      reset();
      return;
    }
    const existing = (catalog ?? []).find((t) => sameName(t.name, name));
    if (existing) {
      await apply(existing);
      return;
    }
    if (!canCreate) {
      setError("Pick an existing tag. Only managers can create new tags.");
      return;
    }
    setSaving(true);
    setError(null);
    let created: AppointmentTag | undefined;
    try {
      created = await createTag({ name, emoji });
      setCatalog((c) => [...(c ?? []), created as AppointmentTag]);
    } catch (e) {
      // Someone else just created it: apply theirs.
      if (isDuplicateTagNameError(e)) {
        created = (await loadCatalog()).find((t) => sameName(t.name, name));
      }
      if (!created) {
        console.error("Failed to create appointment tag:", e);
        setError("Couldn't create the tag. Try again.");
      }
    } finally {
      setSaving(false);
    }
    if (created) await apply(created);
  };

  const query = draft.trim();
  const options = useMemo(() => {
    if (!adding || !catalog) return [];
    const q = query.toLowerCase();
    return catalog
      .filter((t) => !tags.some((x) => x.id === t.id))
      .filter((t) => !q || t.name.toLowerCase().includes(q));
  }, [adding, catalog, query, tags]);
  const emptyLabel =
    catalog === null
      ? "Loading tags…"
      : query && canCreate
        ? `Enter to create “${query}”`
        : query
          ? "No matching tags"
          : "No tags yet";

  return (
    <Box className={styles.row} data-testid="appointment-tags-row">
      <Group gap={6} wrap="wrap" align="center">
        {tags.map((tag) => (
          <Box
            key={tag.id}
            component="span"
            className={styles.chip}
            data-testid="appointment-tag"
          >
            {tag.emoji ? (
              <Text component="span" className={styles.chipEmoji} aria-hidden>
                {tag.emoji}
              </Text>
            ) : (
              <IconTag size={12} stroke={2.2} aria-hidden />
            )}
            <Text component="span" className={styles.chipName}>
              {tag.name}
            </Text>
            <Tooltip label="Remove tag" withinPortal>
              <ActionIcon
                variant="transparent"
                size={16}
                radius="xl"
                className={styles.remove}
                aria-label={`Remove tag ${tag.name}`}
                disabled={saving}
                onClick={(e) => {
                  e.stopPropagation();
                  remove(tag);
                }}
              >
                <IconX size={12} />
              </ActionIcon>
            </Tooltip>
          </Box>
        ))}

        {adding ? (
          <Group gap={6} wrap="nowrap">
            <Combobox
              store={combobox}
              position="bottom-start"
              shadow="md"
              radius={10}
              width={220}
              withinPortal
              onOptionSubmit={(id) => {
                const tag = catalog?.find((t) => t.id === id);
                if (tag) void apply(tag);
              }}
            >
              <Combobox.Target>
                <TextInput
                  value={draft}
                  onChange={(e) => {
                    setDraft(e.currentTarget.value);
                    combobox.openDropdown();
                    combobox.updateSelectedOptionIndex();
                  }}
                  onClick={() => combobox.openDropdown()}
                  onKeyDown={(e) => {
                    // A highlighted option: Combobox picks it on Enter.
                    if (
                      e.key === "Enter" &&
                      combobox.getSelectedOptionIndex() === -1
                    ) {
                      e.preventDefault();
                      void commit();
                    }
                    if (e.key === "Escape") {
                      // The input owns Escape: cancel, don't close the drawer.
                      e.preventDefault();
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();
                      reset();
                    }
                  }}
                  autoFocus
                  placeholder="Tag name"
                  aria-label="New tag"
                  maxLength={MAX_APPOINTMENT_TAG_NAME_LENGTH}
                  disabled={saving}
                  size="xs"
                  radius="xl"
                  w={180}
                  classNames={{ input: styles.input }}
                  // 14px: the theme's 1rem input size is an inline style.
                  styles={{ input: { fontSize: 14 } }}
                  leftSection={
                    canCreate ? (
                      <TagEmojiPicker value={emoji} onChange={setEmoji} />
                    ) : (
                      <IconTag size={14} stroke={2} />
                    )
                  }
                  leftSectionPointerEvents={canCreate ? "all" : "none"}
                />
              </Combobox.Target>
              <Combobox.Dropdown p={4}>
                <Combobox.Options mah={220} className={styles.options}>
                  {options.map((tag) => (
                    <Combobox.Option
                      key={tag.id}
                      value={tag.id}
                      className={styles.suggestion}
                    >
                      {tag.emoji ? `${tag.emoji} ${tag.name}` : tag.name}
                    </Combobox.Option>
                  ))}
                  {options.length === 0 && (
                    <Combobox.Empty>{emptyLabel}</Combobox.Empty>
                  )}
                </Combobox.Options>
              </Combobox.Dropdown>
            </Combobox>
            <Button
              size="compact-xs"
              radius="xl"
              color="teal"
              className={styles.addBtn}
              onClick={() => void commit()}
              loading={saving}
              aria-label="Add tag"
            >
              Add
            </Button>
            <ActionIcon
              variant="subtle"
              color="gray"
              radius="xl"
              size={28}
              aria-label="Cancel"
              onClick={reset}
              disabled={saving}
            >
              <IconX size={14} />
            </ActionIcon>
          </Group>
        ) : (
          <UnstyledButton className={styles.addTag} onClick={start}>
            <IconPlus size={13} />
            Add tag
          </UnstyledButton>
        )}
      </Group>
      {error && (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      )}
    </Box>
  );
}
